import { Injectable } from "@angular/core";
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { AppService } from "./app.service";
import { SignalRService } from './signalr.service';
import { RequestsType } from "../model/appConfig";
import { IAuthResponse, IChildrenResponse } from "../model/auth.response.interface";
import { User } from "../model/user.model";
import * as Sentry from '@sentry/browser';

@Injectable()
export class AuthService {
    public authenticated: boolean = false;
    public user: User = (new User()).init();
    public currentUser: User = (new User()).init();
    public prevUser: User = (new User()).init();

    constructor(
        private apiService: ApiService,
        public signalRService: SignalRService,
        private appService: AppService,
    ) {
        if (localStorage.getItem("user") && localStorage.getItem("currentuser")) {
            this.user = JSON.parse(localStorage.getItem("user"));
            this.currentUser = JSON.parse(localStorage.getItem("currentuser"));
            this.authenticated = true;
            // Set Sentry user for restored session
            Sentry.setUser({ id: this.currentUser.name, username: this.currentUser.name });
        }

        if (localStorage.getItem("prevuser")) {
            this.prevUser = JSON.parse(localStorage.getItem("prevuser"));
        }
    }

    public authenticate(name: string, password: string): Observable<IAuthResponse> {
        return this.apiService.authenticate(name, password);
    }

    public getChildUsers(name: string, password: string): Observable<IChildrenResponse[]> {
        return this.apiService.getChildUsers(name, password);
    }

    public saveAuthData(
        name: string,
        password: string,
        children: User[],
        isChildUser: boolean,
        permissions?: {
            canControlSchedulerSwitch?: boolean;
            canEditWeeklyScheduler?: boolean;
            canEditSpecialDates?: boolean;
        }
    ): void {
        if (!isChildUser) {
            this.user.name = name;
            this.user.password = password;
            this.user.children = children;
            this.user.active = true;
            if (permissions) {
                this.user.canControlSchedulerSwitch = permissions.canControlSchedulerSwitch;
                this.user.canEditWeeklyScheduler = permissions.canEditWeeklyScheduler;
                this.user.canEditSpecialDates = permissions.canEditSpecialDates;
            }
            localStorage.setItem("user", JSON.stringify(this.user));

            for (const field in this.user) {
                this.currentUser[field] = this.user[field];
            }

            localStorage.setItem("currentuser", JSON.stringify(this.currentUser));
        }
        else {
            this.currentUser.name = name;
            this.currentUser.password = password;
            this.currentUser.isChildUser = true;
            if (permissions) {
                this.currentUser.canControlSchedulerSwitch = permissions.canControlSchedulerSwitch;
                this.currentUser.canEditWeeklyScheduler = permissions.canEditWeeklyScheduler;
                this.currentUser.canEditSpecialDates = permissions.canEditSpecialDates;
            }
            localStorage.setItem("currentuser", JSON.stringify(this.currentUser));
        }

        this.authenticated = true;
        // Set Sentry user after successful login
        Sentry.setUser({ id: this.currentUser.name, username: this.currentUser.name });
    }

    public async logout(): Promise<void> {
        this.prevUser = this.currentUser.isChildUser ? this.user : this.currentUser;
        localStorage.setItem("prevuser", JSON.stringify(this.prevUser));
        this.user = (new User()).init();
        this.currentUser = (new User()).init();
        localStorage.removeItem("user");
        localStorage.removeItem("currentuser");
        this.authenticated = false;
        // Clear Sentry user on logout
        Sentry.setUser(null);
        
        try {
            const config = await this.appService.getConfig();
            if (config.requestsType === RequestsType.SignalR) {
                this.signalRService.disconnect();
            }
        } catch (error) {
            console.error('Failed to get config during logout:', error);
        }
    }

    public setCurrentUser(user: User): void {
        this.currentUser = user;
        localStorage.setItem("currentuser", JSON.stringify(this.currentUser));
        // Update Sentry user when switching active user
        Sentry.setUser({ id: this.currentUser.name, username: this.currentUser.name });
    }
}